import { apiGet, apiPatch, apiPost, apiPut } from '@/api/mutator';

import type {
  BranchResponse,
  BranchSlugAvailability,
  BranchUsageResponse,
  CreateBranchInput,
  MyBranchesResponse,
  StaffBranchesResponse,
  UpdateBranchInput,
} from './contract';

/**
 * ════════════════════════════════════════════════════════════════════════════
 *  THE BRANCH ROUTES (multi-branch design §5)
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Hand-written callers through the panel's one HTTP client (`api/mutator.ts`
 * — version prefix, `{ data }` unwrapping, Persian errors, single-flight
 * refresh). They exist because the committed OpenAPI snapshot predates these
 * routes; after checkpoint C1 each becomes the generated function of the same
 * route, and the screens do not change.
 *
 * | Screen                     | Route                              | Permission       | Plan         |
 * |----------------------------|------------------------------------|------------------|--------------|
 * | Branch switcher, pickers   | GET  /branches/mine                | any staff        | every plan   |
 * | Settings → شعبه‌ها          | GET  /branches?includeArchived     | settings.view    | every plan   |
 * | «{used} از {max} شعبه»     | GET  /branches/usage               | settings.view    | every plan   |
 * | Slug field                 | GET  /branches/slug-availability   | branch.manage    | every plan   |
 * | «افزودن شعبه»              | POST /branches                     | branch.manage    | branches + limit |
 * | Edit                       | PATCH /branches/:id                | branch.manage    | every plan   |
 * | «بایگانی»                  | POST /branches/:id/archive         | branch.manage    | every plan   |
 * | «بازگردانی»                | POST /branches/:id/restore         | branch.manage    | branches + limit |
 * | «شعبهٔ اصلی بشه»           | POST /branches/:id/make-primary    | branch.manage    | every plan   |
 * | Staff drawer               | PUT  /staff/:id/branches           | staff.edit       | adding a non-primary is gated |
 *
 * Every refusal arrives as an `ApiError` with the server's Persian sentence;
 * `reasons.ts` words the ones the panel can predict before the click.
 */

export function fetchMyBranches(signal?: AbortSignal): Promise<MyBranchesResponse> {
  return apiGet<MyBranchesResponse>('/branches/mine', undefined, signal);
}

export function fetchBranches(
  includeArchived: boolean,
  signal?: AbortSignal,
): Promise<BranchResponse[]> {
  // A boolean query flag: `@BooleanQuery()` on the server reads 'true' / 'false'.
  return apiGet<BranchResponse[]>(
    '/branches',
    includeArchived ? { includeArchived: 'true' } : undefined,
    signal,
  );
}

export function fetchBranchUsage(signal?: AbortSignal): Promise<BranchUsageResponse> {
  return apiGet<BranchUsageResponse>('/branches/usage', undefined, signal);
}

export function checkBranchSlug(
  slug: string,
  exceptBranchId: string | null,
  signal?: AbortSignal,
): Promise<BranchSlugAvailability> {
  return apiGet<BranchSlugAvailability>(
    '/branches/slug-availability',
    { slug, ...(exceptBranchId ? { exceptBranchId } : {}) },
    signal,
  );
}

export const branchApi = {
  create: (input: CreateBranchInput) => apiPost<BranchResponse>('/branches', input),
  update: (id: string, input: UpdateBranchInput) =>
    apiPatch<BranchResponse>(`/branches/${encodeURIComponent(id)}`, input),
  archive: (id: string) => apiPost<BranchResponse>(`/branches/${encodeURIComponent(id)}/archive`),
  restore: (id: string) => apiPost<BranchResponse>(`/branches/${encodeURIComponent(id)}/restore`),
  makePrimary: (id: string) =>
    apiPost<BranchResponse>(`/branches/${encodeURIComponent(id)}/make-primary`),
};

/** Replaces a colleague's whole set of branches (≥ 1). */
export function setStaffBranches(
  staffId: string,
  branchIds: readonly string[],
): Promise<StaffBranchesResponse> {
  return apiPut<StaffBranchesResponse>(`/staff/${encodeURIComponent(staffId)}/branches`, {
    branchIds: [...branchIds],
  });
}
